import { SxProps, useTheme } from '@mui/material';
import MuiPopper from '@mui/material/Popper';
import {
  ComponentProps,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ToolbarPlugin } from '@/components/TextEditor/plugins/ToolbarPlugin/ToolbarPlugin';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';
import { zIndexes } from '@/styles/zIndexes';

import { SlideEditorBodyToolbar } from './SlideEditor.bodyToolbar';

/**
 * Get the rich text editor toolbar for the sidebar
 */
export function getSlideEditorSidebarToolbarPlugin() {
  function SidebarToolbarPlugin({
    textEditorKind,
  }: ComponentProps<typeof ToolbarPlugin>) {
    return (
      <LuminaryThemeProvider>
        <ToolbarPlugin
          textEditorKind={textEditorKind}
          generateAIButtonDisabled
        />
      </LuminaryThemeProvider>
    );
  }
  return SidebarToolbarPlugin;
}

export function useSlideEditorSidebarToolbarPlugin() {
  function SidebarToolbarPlugin({
    textEditorKind,
  }: ComponentProps<typeof ToolbarPlugin>) {
    return (
      <LuminaryThemeProvider>
        <ToolbarPlugin
          textEditorKind={textEditorKind}
          generateAIButtonDisabled
        />
      </LuminaryThemeProvider>
    );
  }

  return useMemo(() => SidebarToolbarPlugin, []);
}

/**
 * Hook that returns a stable toolbar plugin component for inline/floating
 * views on slides. Uses refs for `additionalButtons` and `endButtons` so
 * that changes to those props don't cause the toolbar to unmount/remount
 * (which would reset all toolbar state like block type and font size).
 */
export function useSlideEditorInlineToolbarPlugin(
  opts?: SlideEditorToolbarOptions
) {
  const {
    spacing = 2,
    toolbarSx = {},
    additionalButtons,
    endButtons,
  } = opts || {};

  const additionalButtonsRef = useRef(additionalButtons);
  additionalButtonsRef.current = additionalButtons;
  const endButtonsRef = useRef(endButtons);
  endButtonsRef.current = endButtons;
  const spacingRef = useRef(spacing);
  spacingRef.current = spacing;
  const toolbarSxRef = useRef(toolbarSx);
  toolbarSxRef.current = toolbarSx;

  // Stable component — created once, reads current values from refs
  const InlineToolbarPlugin = useCallback(function InlineToolbarPlugin({
    textEditorKind,
  }: ComponentProps<typeof ToolbarPlugin>) {
    // Use a callback ref so that setting the anchor triggers a re-render,
    // which lets the Popper open once the anchor element is in the DOM.
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const theme = useTheme();
    const spacingInt = parseInt(theme.spacing(spacingRef.current).slice(0, -2));

    const offsetModifier = useMemo(
      () => ({
        name: 'offset' as const,
        options: { offset: [0, isNaN(spacingInt) ? 0 : spacingInt] },
      }),
      [spacingInt]
    );

    return (
      <LuminaryThemeProvider>
        {/* Zero-height anchor that stays in the normal flow inside the
              scaled slide. The Popper portals the toolbar to the document
              body so it escapes any ancestor overflow:clip. */}
        <div ref={setAnchorEl} />
        <MuiPopper
          open={!!anchorEl}
          anchorEl={anchorEl}
          placement="top-start"
          data-slide-editor-toolbar
          modifiers={[offsetModifier]}
          sx={{ zIndex: zIndexes.POPOVER, ...toolbarSxRef.current }}
        >
          <SlideEditorBodyToolbar
            textEditorKind={textEditorKind}
            additionalButtons={additionalButtonsRef.current}
            endButtons={endButtonsRef.current}
          />
        </MuiPopper>
      </LuminaryThemeProvider>
    );
  }, []);

  return InlineToolbarPlugin;
}

interface SlideEditorToolbarOptions {
  spacing?: number;
  toolbarSx?: SxProps;
  /** Additional buttons rendered before the default block/format/table buttons */
  additionalButtons?: ReactNode;
  /** Additional buttons rendered after the default block/format/table buttons */
  endButtons?: ReactNode;
}
